import { useAuth } from "react-oidc-context";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { createVendor, editVendor, fetchVendor, fetchVendors, selectVendorStatus, Vendor, VendorApiPayload } from "../vendorsSlice";
import { clearAlert, errorAlert, infoAlert } from "../../shared/appSlice";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { isFulfilled } from "@reduxjs/toolkit";
import { VendorEntryEditor } from "./VendorEntryEditor";
import styled from 'styled-components'
import { InputRow } from "../../shared/components/widgets/InputRow";
import { Loading } from "../../shared/components/Loading";
import { Alert } from "../../shared/components/Alert";
import { FileUploader } from "../../shared/components/widgets/FileUploader";

export const Wrapper = styled.article`
  display: grid;
  place-items: center;
  padding: 3rem;
  width: 100%;
`

export const VendorEntryForm = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const appstate = useAppSelector(state => state.appstate);
  const vendorsstate = useAppSelector(state => state.vendorsstate);
  const vendorStatus = useAppSelector(selectVendorStatus);
  const auth = useAuth();

  const { control, handleSubmit, setValue, formState: { isSubmitSuccessful } } = useForm<Vendor>({ defaultValues: vendorsstate.vendor });

  const [openDialogue, setOpenDialogue] = useState(false);
  const [selectedFile, setSelectedFile] = useState('');

  useEffect(() => {
    dispatch(clearAlert());
    if (id) {
      dispatch(fetchVendor({ id: id } as VendorApiPayload));
    }
  }, [dispatch])

  useEffect(() => {
    if (vendorsstate.vendor?.imageUrl) {
      setSelectedFile(vendorsstate.vendor.imageUrl);
    }
  }, [vendorsstate.vendor])

  useEffect(() => {
    setValue('imageUrl', selectedFile);
  }, [selectedFile, setValue])

  const onSubmit: SubmitHandler<Vendor> = async (data: Vendor) => {
    data.vendorOrder = vendorsstate.vendorOrderValue;

    if (!auth.isAuthenticated) {
      dispatch(errorAlert('You must be logged in to save a vendor.'));
      return;
    }

    if (data.id) {
      const payload: VendorApiPayload = { accessToken: auth.user?.access_token as string, id: data.id, vendor: data }
      const action1 = await dispatch(editVendor(payload))
      if (isFulfilled(action1)) {
        dispatch(infoAlert('vendor updated'));
        const action2 = await dispatch(fetchVendors())
        if (isFulfilled(action2)) {
          navigate('/vendors')
        }
      } else {
        dispatch(errorAlert('Failed to update vendor. Please try again.'));
      }
    } else {
      data.author = auth.user?.profile.preferred_username as string || auth.user?.profile.email as string || auth.user?.profile.sub as string;
      data.category = data.category ? data.category : '';
      data.website = data.website ? data.website : '';
      const payload: VendorApiPayload = { accessToken: auth.user?.access_token as string, id: null, vendor: data }
      const action1 = await dispatch(createVendor(payload))
      if (isFulfilled(action1)) {
        dispatch(infoAlert('vendor created'));
        const action2 = await dispatch(fetchVendors())
        if (isFulfilled(action2)) {
          navigate('/vendors')
        }
      } else {
        dispatch(errorAlert('Failed to create vendor. Please try again.'));
      }
    }
  }

  return (
    vendorStatus === 'pending' ? (
      <Loading centre />
    ) : (
      <Wrapper>
        {appstate.showAlert && <Alert />}

        <form className="lr-padded-container" onSubmit={handleSubmit(onSubmit)}>

          <Controller name="name" rules={{ required: 'name is required' }} control={control} render={({ field, formState: { errors } }) =>
            <InputRow {...field} value={field.value} error={errors.name?.message} type="input" className="form-input" label="Name *" />} />

          <Controller name="category" control={control} render={({ field, formState: { errors } }) =>
            <InputRow {...field} value={field.value} error={errors.category?.message} type="input" className="form-input" label="Category" />} />

          <Controller name="website" control={control} render={({ field, formState: { errors } }) =>
            <InputRow {...field} value={field.value} error={errors.website?.message} type="input" className="form-input" label="Website" />} />

          <Controller
            name='description'
            control={control}
            render={({ field: { onChange, ...field }, formState: { errors } }) => (
              <VendorEntryEditor {...field} label="Description" value={field.value} setValue={setValue} onChange={onChange} error={errors.description?.message} />
            )}
          />

          <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>Image</label>
            {selectedFile && (
              <img src={selectedFile} alt="preview" style={{ maxWidth: '200px', display: 'block', marginBottom: '0.5rem', borderRadius: '4px' }} />
            )}
            <button type="button" className="btn" onClick={() => setOpenDialogue(true)}>
              {selectedFile ? 'Change Image' : 'Upload Image'}
            </button>
            <FileUploader openDialogue={openDialogue} setOpenDialogue={setOpenDialogue} selectedFile={selectedFile} setSelectedFile={setSelectedFile} />
          </div>

          <button type='submit' className='btn btn-go'>
            Submit
          </button>
        </form>
      </Wrapper>
    )
  );
}
